// @ts-nocheck
// app/methodology.tsx — Editorial methodology page

import Head from 'expo-router/head';
import { Platform, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { ArrowLeft, Dot } from 'lucide-react-native';

import { theme, ACCENT, INK, INK_MUTED, INK_SOFT, PAPER, PAPER_SOFT } from '@/constants/theme';
import {
  DoubleRule, EditorialButton, EditorialFooter, Eyebrow, Headline, Numeral, Paper, PageHero, Section, Title,
} from '@/components/editorial';
import { EditorialHeader } from '@/components/editorial/EditorialHeader';
import { AccessibilityButton } from '@/components/AccessibilityButton';

const SITE_URL = 'https://kursiaii.com';
const MONO = theme.fonts.mono;
const SERIF = theme.fonts.serif;
const SERIF_BOLD = theme.fonts.serifBold;
const SERIF_BLACK = theme.fonts.serifBlack;
const SANS = theme.fonts.regular;
const IS_WEB = Platform.OS === 'web';

const breadcrumbSchema = JSON.stringify({
  '@context': 'https://schema.org', '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'דף הבית', item: `${SITE_URL}/` },
    { '@type': 'ListItem', position: 2, name: 'מתודולוגיה', item: `${SITE_URL}/methodology` },
  ],
});

const faqSchema = JSON.stringify({
  '@context': 'https://schema.org', '@type': 'FAQPage',
  mainEntity: [
    { '@type': 'Question', name: 'מאיפה Kursi מקבלת מידע על קורסים?', acceptedAnswer: { '@type': 'Answer', text: 'מאתרי ספקים פומביים, מחומרים שמועברים ישירות על ידי ספקים ושותפים, ומסקירה שוטפת של השוק.' } },
    { '@type': 'Question', name: 'כמה פעמים הקטלוג מתעדכן?', acceptedAnswer: { '@type': 'Answer', text: 'הקטלוג מתעדכן באופן שוטף. מחירים ומועדי פתיחה משתנים לעיתים — מומלץ לאמת ישירות מול הספק.' } },
    { '@type': 'Question', name: 'האם Kursi ממליצה על ספק ספציפי?', acceptedAnswer: { '@type': 'Answer', text: 'לא. נתונים אובייקטיביים בלבד. שיתופי פעולה עסקיים מסומנים באופן ברור.' } },
    { '@type': 'Question', name: 'איך עובד מנגנון ההתאמה?', acceptedAnswer: { '@type': 'Answer', text: 'משתמש כותב מטרה ואילוצים. AI מתאים את הקורסים מהקטלוג שמתאימים ביותר.' } },
    { '@type': 'Question', name: 'מה לא מובטח?', acceptedAnswer: { '@type': 'Answer', text: 'דיוק מלא של מחירים, זמינות מקומות, השמה לעבודה, או התאמה מושלמת אוטומטית. נדרשת בדיקה מול הספק.' } },
  ],
});

const SECTIONS = [
  {
    n: '01', title: 'איך Kursi עובדת',
    body: 'Kursi היא פלטפורמת חיפוש והשוואה — לא חנות קורסים ולא מכללה. המשתמש כותב מטרה ואילוצים, והפלטפורמה מחזירה רשימה קצרה של התאמות מתוך קטלוג של מעל 1,200 קורסים.',
    bullets: ['המשתמש כותב מטרה בשפה חופשית', 'ה-AI מנתח ומחזיר 3 התאמות', 'ניתן לחדד את הבקשה לתוצאות מדויקות יותר', 'לאחר בחירה — המשתמש פונה ישירות לספק'],
  },
  {
    n: '02', title: 'מאיפה מגיע המידע',
    body: 'נתוני הקורסים מגיעים ממקורות מגוונים שנבדקים ומעודכנים באופן שוטף.',
    bullets: ['אתרי הספקים הרשמיים — מידע פומבי', 'חומרים שמועברים ישירות על ידי ספקים ושותפים', 'סקירה שוטפת של שוק הקורסים הישראלי', 'עדכונים לפחות אחת לחודש לתחומים פעילים'],
  },
  {
    n: '03', title: 'תדירות עדכון',
    body: 'הקטלוג מתעדכן באופן שוטף. מחירים ומועדי פתיחה עשויים להשתנות — אנו ממליצים תמיד לאמת פרטים ישירות מול הספק.',
    bullets: ['עדכון שוטף של קורסים חדשים', 'מחירים: יכולים להשתנות — אמתו מול הספק', 'מועדי פתיחה: יכולים להשתנות — אמתו מול הספק', 'תוכן קורסים: מתעדכן לפי מידע זמין'],
  },
  {
    n: '04', title: 'אובייקטיביות ושקיפות',
    body: 'Kursi לא ממליצה על ספק ספציפי. כאשר קיים שיתוף פעולה עסקי — הדבר מסומן במפורש. ההחלטה הסופית תמיד אצל המשתמש.',
    bullets: ['אין "מקום ראשון מקודם" סמוי', 'שיתופי פעולה והטבות — מסומנים במפורש', 'לא מוכרים פרסום במסווה של המלצה', 'כל ספק מוצג בתבנית אחידה'],
  },
  {
    n: '05', title: 'מגבלות ומה לא מובטח',
    body: 'חשוב שמשתמשים יבינו את גבולות הפלטפורמה לפני קבלת החלטה.',
    bullets: ['מחירים: לא מובטחים — בדקו מול הספק', 'זמינות מקומות: לא ידועה בזמן אמת', 'השמה לעבודה: לא מובטחת ולא קשורה ל-Kursi', 'התאמה מושלמת: לא קיימת — נקודת התחלה, לא החלטה'],
  },
];

const FACTS = [
  { n: '1,200+', label: 'קורסים בקטלוג' },
  { n: '01—31', label: 'עדכונים יומיים' },
  { n: '00', label: 'ספונסרים בראש' },
];

export default function MethodologyPage() {
  const router = useRouter();
  return (
    <Paper>
      <AccessibilityButton />
      {IS_WEB && (
        <Head>
          <title>מתודולוגיה ואמינות · Kursi</title>
          <meta name="description" content="איך Kursi עובדת, מאיפה מגיע המידע, איך אנחנו שומרים על שקיפות ומה לא מובטח." />
          <link rel="canonical" href={`${SITE_URL}/methodology`} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: faqSchema }} />
        </Head>
      )}

      <ScrollView style={{ flex: 1, backgroundColor: PAPER }} contentContainerStyle={{ backgroundColor: PAPER }}>
        <EditorialHeader />

        <Section py="xl" px="md" max={1100}>
          <PageHero
            kicker="גיליון השקיפות · עיתון Kursi"
            title={<>איך אנחנו עובדים{'\n'}<Text style={{ color: ACCENT, fontStyle: 'italic' as any }}>בלי לעבוד עליכם.</Text></>}
            lede="חמישה עקרונות, מקור מידע אחד, ושקיפות מלאה. הנה כל מה שצריך לדעת לפני שאתם מסתמכים על ההמלצות שלנו."
            meta="קריאת חובה · עודכן 2025"
          />
        </Section>

        <Section py="lg" px="md" max={1100}>
          <View style={s.factsRow}>
            {FACTS.map((f, i) => (
              <View key={i} style={[s.factCol, i > 0 && { borderRightWidth: 1, borderRightColor: INK }]}>
                <Numeral size={64}>{f.n}</Numeral>
                <View style={{ height: 6 }} />
                <Text style={s.factLabel}>{f.label}</Text>
              </View>
            ))}
          </View>
        </Section>

        <Section py="md" px="md" max={1100}><DoubleRule /></Section>

        <Section py="xxl" px="md" max={1100}>
          {SECTIONS.map((sec, i) => (
            <View key={i} style={s.section}>
              <View style={s.sectionHead}>
                <Numeral size={80}>{sec.n}</Numeral>
                <View style={{ flex: 1, paddingRight: 24 }}>
                  <Title level={2}>{sec.title}</Title>
                  <View style={{ height: 12 }} />
                  <Text style={s.body}>{sec.body}</Text>
                </View>
              </View>
              <View style={{ height: 18 }} />
              <View style={s.bulletsBlock}>
                {sec.bullets.map((b, j) => (
                  <View key={j} style={s.bulletRow}>
                    <Text style={s.bulletMark}>—</Text>
                    <Text style={s.bulletText}>{b}</Text>
                  </View>
                ))}
              </View>
            </View>
          ))}
        </Section>

        <Section py="xxl" px="md" max={1100}>
          <View style={{ alignItems: 'center' }}>
            <Headline level={2} align="center">קראתם. השתכנעתם?</Headline>
            <View style={{ height: 14 }} />
            <Text style={[s.body, { textAlign: 'center', maxWidth: 540 }]}>הדרך הכי קצרה לבדוק את השיטה — לנסות אותה.</Text>
            <View style={{ height: 24 }} />
            <EditorialButton label="התחל התאמה" onPress={() => router.push('/chat')} variant="primary" size="lg" trailing={<ArrowLeft size={16} color={PAPER} />} />
          </View>
        </Section>

        <EditorialFooter onNavigate={(href) => router.push(href as any)} />
      </ScrollView>
    </Paper>
  );
}

const s = StyleSheet.create({
  factsRow: { flexDirection: 'row-reverse', borderWidth: 1, borderColor: INK, backgroundColor: PAPER_SOFT } as any,
  factCol: { flex: 1, padding: 28, alignItems: 'flex-end' },
  factLabel: { fontFamily: MONO, fontSize: 11, color: INK_MUTED, letterSpacing: 1.6, textTransform: 'uppercase' as any, textAlign: 'right' },

  section: { paddingVertical: 36, borderTopWidth: 1, borderTopColor: INK },
  sectionHead: { flexDirection: 'row-reverse', alignItems: 'flex-start' } as any,
  body: { fontFamily: SANS, fontSize: 17, lineHeight: 28, color: INK_SOFT, textAlign: 'right' },

  bulletsBlock: { paddingRight: 104, marginTop: 8 },
  bulletRow: { flexDirection: 'row-reverse', gap: 12, paddingVertical: 4 } as any,
  bulletMark: { fontFamily: SERIF_BOLD, fontSize: 18, color: ACCENT, lineHeight: 28 },
  bulletText: { flex: 1, fontFamily: SANS, fontSize: 16, lineHeight: 28, color: INK, textAlign: 'right' },
});
